import { memo } from 'react';

import { ComponentContextProps, merge, renderWithContext, useComponent } from '@/components';
import { DivProps } from '@/utils';

export type LogoProps = DivProps &
  Partial<ComponentContextProps> & {
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  };

const ILogo = ({ size, variant, className, children, ...rest }: LogoProps) => {
  const context = useComponent({ variant });

  let sizeStyle;
  switch (size) {
    case 'xs':
      sizeStyle = 'size-4 rounded-xs';
      break;
    /* default size is "sm" */
    case undefined:
    case 'sm':
      sizeStyle = 'size-5 rounded-sm p-[0.125rem]';
      break;
    case 'md':
      sizeStyle = 'size-6 rounded-md p-[0.125rem]';
      break;
    case 'lg':
      sizeStyle = 'size-7 rounded-md p-[0.35rem]';
      break;
    case 'xl':
      sizeStyle = 'size-9 rounded-md p-[0.5rem]';
      break;
    case '2xl':
      sizeStyle = 'size-11 rounded-lg p-[0.5rem]';
      break;
  }

  /* prettier-ignore */
  const styles = [
    'flex',
    'justify-center',
    'items-center',
    'group/logo',
    'overflow-hidden',
    sizeStyle,
  ];

  /* prettier-ignore */
  const plainStyles = [
    'bg-white',
    'border-gray-200',
    'border-1',
    'shadow-xs',
    'rounded-sm',
  ];

  /* prettier-ignore */
  const primaryStyles = [
    'bg-primary-400',
  ];

  /* prettier-ignore */
  const formatterStyles = [
    'bg-purple-50',
  ];

  /* prettier-ignore */
  const validatorStyles = [
    'bg-success-500',
  ];

  /* prettier-ignore */
  const errorStyles = [
    'bg-error-100',
    'text-error-500',
  ];

  return (
    <div
      className={merge(
        styles,
        context.variant === 'default' && plainStyles,
        context.variant === 'plain' && plainStyles,
        context.variant === 'primary' && primaryStyles,
        context.variant === 'formatter' && formatterStyles,
        context.variant === 'validator' && validatorStyles,
        context.variant === 'error' && errorStyles,
        className
      )}
      {...rest}
    >
      {renderWithContext(children, context)}
    </div>
  );
};

export const Logo = memo(ILogo) as typeof ILogo;
